import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { racesAPI } from '../../services/api';

function SubmitResults() {
  const { raceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [race, setRace] = useState(null);
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);
  
  // Legacy mode state
  const [formData, setFormData] = useState({
    winner: '',
    place: '',
    show: '',
    finishTime: ''
  });

  // Advanced mode state
  const [standings, setStandings] = useState([]);
  const [payouts, setPayouts] = useState({
    win: { horseId: '', wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 },
    place: [],
    show: [],
    exacta: { wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 },
    trifecta: { wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 }
  });
  const [finishTime, setFinishTime] = useState('');

  const loadRace = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await racesAPI.getRaceById(raceId);
      if (response.success) {
        setRace(response.data);
        
        // Initialize standings array with all horses
        if (response.data.horses && response.data.horses.length > 0) {
          const initialStandings = response.data.horses
            .filter(h => !h.scratched)
            .map((h, idx) => ({
              position: idx + 1,
              horseId: h.horseId,
              horseName: h.horseName
            }));
          setStandings(initialStandings);
          
          // Initialize place payouts (top 2)
          if (initialStandings.length >= 2) {
            setPayouts(prev => ({
              ...prev,
              win: { ...prev.win, horseId: initialStandings[0].horseId, horseName: initialStandings[0].horseName },
              place: [
                { horseId: initialStandings[0].horseId, horseName: initialStandings[0].horseName, wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 },
                { horseId: initialStandings[1].horseId, horseName: initialStandings[1].horseName, wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 }
              ],
              show: initialStandings.length >= 3 ? [
                { horseId: initialStandings[0].horseId, horseName: initialStandings[0].horseName, wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 },
                { horseId: initialStandings[1].horseId, horseName: initialStandings[1].horseName, wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 },
                { horseId: initialStandings[2].horseId, horseName: initialStandings[2].horseName, wagerAmount: 2.00, actualWagerAmount: 2.00, payoutAmount: 0 }
              ] : []
            }));
          }
        }
        
        // Check if race already has results
        if (response.data.status === 'completed') {
          setError('This race already has results submitted.');
        }
      } else {
        setError('Failed to load race');
      }
    } catch (err) {
      console.error('Failed to load race:', err);
      setError('Failed to load race details');
    } finally {
      setLoading(false);
    }
  }, [raceId]);

  useEffect(() => {
    loadRace();
  }, [loadRace]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!useAdvancedMode && !formData.winner) {
      setError('Winner is required');
      return;
    }

    if (useAdvancedMode && standings.length === 0) {
      setError('Please arrange horse standings');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      let resultsData;

      if (useAdvancedMode) {
        // Advanced mode: send full standings and payout data
        resultsData = {
          standings: standings.map((s, idx) => ({
            position: idx + 1,
            horseId: s.horseId,
            horseName: s.horseName
          })),
          payouts: {
            win: {
              horseId: standings[0].horseId,
              horseName: standings[0].horseName,
              wagerAmount: parseFloat(payouts.win.wagerAmount) || 2.00,
              actualWagerAmount: parseFloat(payouts.win.actualWagerAmount) || 2.00,
              payoutAmount: parseFloat(payouts.win.payoutAmount) || 0
            },
            place: standings.length >= 2 ? payouts.place.map((p, idx) => ({
              horseId: standings[idx].horseId,
              horseName: standings[idx].horseName,
              wagerAmount: parseFloat(p.wagerAmount) || 2.00,
              actualWagerAmount: parseFloat(p.actualWagerAmount) || 2.00,
              payoutAmount: parseFloat(p.payoutAmount) || 0
            })) : [],
            show: standings.length >= 3 ? payouts.show.map((s, idx) => ({
              horseId: standings[idx].horseId,
              horseName: standings[idx].horseName,
              wagerAmount: parseFloat(s.wagerAmount) || 2.00,
              actualWagerAmount: parseFloat(s.actualWagerAmount) || 2.00,
              payoutAmount: parseFloat(s.payoutAmount) || 0
            })) : [],
            exacta: {
              wagerAmount: parseFloat(payouts.exacta.wagerAmount) || 2.00,
              actualWagerAmount: parseFloat(payouts.exacta.actualWagerAmount) || 2.00,
              payoutAmount: parseFloat(payouts.exacta.payoutAmount) || 0
            },
            trifecta: {
              wagerAmount: parseFloat(payouts.trifecta.wagerAmount) || 2.00,
              actualWagerAmount: parseFloat(payouts.trifecta.actualWagerAmount) || 2.00,
              payoutAmount: parseFloat(payouts.trifecta.payoutAmount) || 0
            }
          },
          finishTime: finishTime
        };
      } else {
        // Legacy mode: send winner/place/show
        resultsData = {
          winner: {
            horseId: formData.winner
          }
        };

        // Add place if selected
        if (formData.place) {
          resultsData.place = {
            horseId: formData.place
          };
        }

        // Add show if selected
        if (formData.show) {
          resultsData.show = {
            horseId: formData.show
          };
        }

        // Add finish time if provided
        if (formData.finishTime) {
          resultsData.finishTime = formData.finishTime;
        }
      }

      const response = await racesAPI.submitRaceResults(raceId, resultsData);
      
      if (response.success) {
        setSuccessMessage(
          `Results submitted successfully! Processed ${response.data.picksProcessed} picks ` +
          `for ${response.data.usersUpdated} users. Total payouts: $${response.data.totalPayouts.toFixed(2)}`
        );
        
        // Reload race to show updated results
        setTimeout(() => {
          loadRace();
        }, 2000);
      } else {
        setError(response.error?.message || 'Failed to submit results');
      }
    } catch (err) {
      console.error('Failed to submit results:', err);
      setError(err.response?.data?.error?.message || 'Failed to submit race results');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const updateStandingPosition = (position, horseId) => {
    const newStandings = [...standings];
    const allHorses = race.horses.filter(h => !h.scratched);
    
    if (horseId === '') {
      // Clear this position
      newStandings[position] = {
        position: position + 1,
        horseId: '',
        horseName: ''
      };
    } else {
      const selectedHorse = allHorses.find(h => h.horseId === horseId);
      
      if (!selectedHorse) return;
      
      // Update the horse at this position
      newStandings[position] = {
        position: position + 1,
        horseId: selectedHorse.horseId,
        horseName: selectedHorse.horseName
      };
    }
    
    setStandings(newStandings);
    
    // Update payout horse IDs based on new positions
    if (newStandings.length > 0 && newStandings[0] && newStandings[0].horseId) {
      setPayouts(prev => ({
        ...prev,
        win: { ...prev.win, horseId: newStandings[0].horseId, horseName: newStandings[0].horseName }
      }));
    }
    
    if (newStandings.length >= 2 && newStandings[0] && newStandings[0].horseId && newStandings[1] && newStandings[1].horseId) {
      setPayouts(prev => ({
        ...prev,
        place: [
          { ...prev.place[0], horseId: newStandings[0].horseId, horseName: newStandings[0].horseName },
          { ...prev.place[1], horseId: newStandings[1].horseId, horseName: newStandings[1].horseName }
        ]
      }));
    }
    
    if (newStandings.length >= 3 && newStandings[0] && newStandings[0].horseId && newStandings[1] && newStandings[1].horseId && newStandings[2] && newStandings[2].horseId) {
      setPayouts(prev => ({
        ...prev,
        show: [
          { ...prev.show[0], horseId: newStandings[0].horseId, horseName: newStandings[0].horseName },
          { ...prev.show[1], horseId: newStandings[1].horseId, horseName: newStandings[1].horseName },
          { ...prev.show[2], horseId: newStandings[2].horseId, horseName: newStandings[2].horseName }
        ]
      }));
    }
  };

  const clearStandingPosition = (position) => {
    updateStandingPosition(position, '');
  };

  const updatePayout = (betType, index, field, value) => {
    setPayouts(prev => {
      const newPayouts = { ...prev };
      
      if (betType === 'win') {
        newPayouts.win = { ...newPayouts.win, [field]: value };
      } else if (betType === 'exacta' || betType === 'trifecta') {
        newPayouts[betType] = { ...newPayouts[betType], [field]: value };
      } else if (betType === 'place' || betType === 'show') {
        const newArray = [...newPayouts[betType]];
        newArray[index] = { ...newArray[index], [field]: value };
        newPayouts[betType] = newArray;
      }
      
      return newPayouts;
    });
  };

  if (loading) {
    return (
      <div className="container">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!race) {
    return (
      <div className="container">
        <h2>Race not found</h2>
        <button onClick={() => navigate('/admin/races')} className="btn btn-primary">
          Back to Races
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <button onClick={() => navigate('/admin/races')} className="btn btn-secondary">
          ← Back to Race Management
        </button>
      </div>

      <h2>Submit Race Results</h2>
      
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3>{race.raceName}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>Venue:</strong> {race.venue}
          </div>
          <div>
            <strong>Location:</strong> {race.location}
          </div>
          <div>
            <strong>Date:</strong> {new Date(race.raceDate).toLocaleString()}
          </div>
          <div>
            <strong>Status:</strong>
            <span className={`race-status ${race.status}`} style={{ marginLeft: '0.5rem' }}>
              {race.status}
            </span>
          </div>
        </div>
      </div>

      {race.status === 'completed' && race.results && (
        <div className="card" style={{ marginBottom: '2rem', background: '#e8f5e9' }}>
          <h3>Current Results — Resubmit below to recalculate payouts</h3>
          <div>
            <p><strong>🏆 Winner:</strong> {race.results.winner?.horseName}</p>
            {race.results.place && (
              <p><strong>2nd Place:</strong> {race.results.place.horseName}</p>
            )}
            {race.results.show && (
              <p><strong>3rd Place:</strong> {race.results.show.horseName}</p>
            )}
            {race.results.winner?.finishTime && (
              <p><strong>Finish Time:</strong> {race.results.winner.finishTime}</p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          {successMessage}
        </div>
      )}

      {/* Form always shown — supports resubmission for completed races */}
      <>
          {/* Mode Toggle */}
          <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={useAdvancedMode}
                onChange={(e) => setUseAdvancedMode(e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold' }}>
                Use Advanced Mode (Enter all standings and actual payouts from track)
              </span>
            </label>
            <small style={{ marginLeft: '28px', color: '#666', display: 'block', marginTop: '0.25rem' }}>
              Advanced mode allows you to enter the complete finishing order and actual payout amounts from the track,
              providing more accurate calculations for exotic bets.
            </small>
          </div>

          <form onSubmit={handleSubmit} className="card">
            <h3>Enter Results</h3>

            {!useAdvancedMode ? (
              /* LEGACY MODE */
              <>
                <div className="form-group">
                  <label htmlFor="winner">Winner (1st Place) *</label>
                  <select
                    id="winner"
                    name="winner"
                    value={formData.winner}
                    onChange={handleChange}
                    required
                    className="form-control"
                  >
                    <option value="">Select winner...</option>
                    {race.horses?.filter(h => !h.scratched).map(horse => (
                      <option key={horse.horseId} value={horse.horseId}>
                        #{horse.postPosition} - {horse.horseName}
                        {horse.jockey && ` (${horse.jockey})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="place">2nd Place (Optional)</label>
                  <select
                    id="place"
                    name="place"
                    value={formData.place}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select 2nd place...</option>
                    {race.horses?.filter(h => !h.scratched).map(horse => (
                      <option 
                        key={horse.horseId} 
                        value={horse.horseId}
                        disabled={horse.horseId === formData.winner}
                      >
                        #{horse.postPosition} - {horse.horseName}
                        {horse.jockey && ` (${horse.jockey})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="show">3rd Place (Optional)</label>
                  <select
                    id="show"
                    name="show"
                    value={formData.show}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select 3rd place...</option>
                    {race.horses?.filter(h => !h.scratched).map(horse => (
                      <option 
                        key={horse.horseId} 
                        value={horse.horseId}
                        disabled={horse.horseId === formData.winner || horse.horseId === formData.place}
                      >
                        #{horse.postPosition} - {horse.horseName}
                        {horse.jockey && ` (${horse.jockey})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="finishTime">Finish Time (Optional)</label>
                  <input
                    type="text"
                    id="finishTime"
                    name="finishTime"
                    value={formData.finishTime}
                    onChange={handleChange}
                    placeholder="e.g., 2:01.25"
                    className="form-control"
                  />
                  <small className="form-text">Format: MM:SS.MS (e.g., 2:01.25)</small>
                </div>
              </>
            ) : (
              /* ADVANCED MODE */
              <>
                {/* Horse Standings */}
                <div style={{ marginBottom: '2rem' }}>
                  <h4>Final Standings (Select horses in finishing order)</h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                    Use the dropdown lists to select which horse finished in each position. Click ✕ to clear a selection.
                  </p>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {standings.map((standing, index) => {
                      const selectedHorseIds = standings.map(s => s?.horseId).filter(Boolean);
                      const currentHorseId = standing?.horseId;
                      
                      return (
                        <div 
                          key={index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '100px 1fr auto',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem',
                            background: index < 3 ? '#f5f5f5' : 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', paddingRight: '0.5rem' }}>
                            {index === 0 && '🥇 1st:'}
                            {index === 1 && '🥈 2nd:'}
                            {index === 2 && '🥉 3rd:'}
                            {index > 2 && `#${index + 1}:`}
                          </div>
                          <select
                            value={currentHorseId || ''}
                            onChange={(e) => updateStandingPosition(index, e.target.value)}
                            className="form-control"
                            style={{
                              fontWeight: index < 3 ? 'bold' : 'normal',
                              fontSize: '1rem',
                              padding: '0.5rem'
                            }}
                          >
                            <option value="">Select horse...</option>
                            {race.horses?.filter(h => !h.scratched).map(horse => (
                              <option 
                                key={horse.horseId} 
                                value={horse.horseId}
                                disabled={selectedHorseIds.includes(horse.horseId) && horse.horseId !== currentHorseId}
                              >
                                #{horse.postPosition} - {horse.horseName}
                                {horse.jockey && ` (${horse.jockey})`}
                              </option>
                            ))}
                          </select>
                          {currentHorseId && (
                            <button
                              type="button"
                              onClick={() => clearStandingPosition(index)}
                              style={{
                                padding: '0.5rem 0.75rem',
                                fontSize: '1.2rem',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                lineHeight: '1',
                                minWidth: '40px'
                              }}
                              title="Clear this selection"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Payout Inputs */}
                <div style={{ marginBottom: '2rem' }}>
                  <h4>Payout Amounts</h4>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                    Enter the actual payout amounts from the track. If the wager is less than $2, enter the actual wager 
                    amount and the system will normalize it to a $2 base.
                  </p>

                  {/* Win Payout */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <h5 style={{ marginTop: 0 }}>Win (1st Place: {standings[0]?.horseName})</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Wager Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.win.actualWagerAmount}
                          onChange={(e) => updatePayout('win', 0, 'actualWagerAmount', e.target.value)}
                          className="form-control"
                          placeholder="2.00"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Payout Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.win.payoutAmount}
                          onChange={(e) => updatePayout('win', 0, 'payoutAmount', e.target.value)}
                          className="form-control"
                          placeholder="5.40"
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '0.85rem', color: '#666' }}>
                        {parseFloat(payouts.win.actualWagerAmount) < 2 && parseFloat(payouts.win.payoutAmount) > 0 && (
                          <div>
                            Normalized to $2: ${((parseFloat(payouts.win.payoutAmount) / parseFloat(payouts.win.actualWagerAmount)) * 2).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Place Payouts */}
                  {standings.length >= 2 && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                      <h5 style={{ marginTop: 0 }}>Place Payouts</h5>
                      {payouts.place.slice(0, 2).map((payout, idx) => (
                        <div key={idx} style={{ marginBottom: idx === 0 ? '1rem' : 0 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            {idx === 0 ? '1st' : '2nd'} Place: {standings[idx]?.horseName}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                              <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Wager</label>
                              <input
                                type="number"
                                step="0.01"
                                value={payout.actualWagerAmount}
                                onChange={(e) => updatePayout('place', idx, 'actualWagerAmount', e.target.value)}
                                className="form-control"
                                placeholder="2.00"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Payout</label>
                              <input
                                type="number"
                                step="0.01"
                                value={payout.payoutAmount}
                                onChange={(e) => updatePayout('place', idx, 'payoutAmount', e.target.value)}
                                className="form-control"
                                placeholder="3.20"
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '0.85rem', color: '#666' }}>
                              {parseFloat(payout.actualWagerAmount) < 2 && parseFloat(payout.payoutAmount) > 0 && (
                                <div>
                                  Normalized: ${((parseFloat(payout.payoutAmount) / parseFloat(payout.actualWagerAmount)) * 2).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show Payouts */}
                  {standings.length >= 3 && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                      <h5 style={{ marginTop: 0 }}>Show Payouts</h5>
                      {payouts.show.slice(0, 3).map((payout, idx) => (
                        <div key={idx} style={{ marginBottom: idx < 2 ? '1rem' : 0 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                            {idx === 0 ? '1st' : idx === 1 ? '2nd' : '3rd'} Place: {standings[idx]?.horseName}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                              <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Wager</label>
                              <input
                                type="number"
                                step="0.01"
                                value={payout.actualWagerAmount}
                                onChange={(e) => updatePayout('show', idx, 'actualWagerAmount', e.target.value)}
                                className="form-control"
                                placeholder="2.00"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Payout</label>
                              <input
                                type="number"
                                step="0.01"
                                value={payout.payoutAmount}
                                onChange={(e) => updatePayout('show', idx, 'payoutAmount', e.target.value)}
                                className="form-control"
                                placeholder="2.60"
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '0.85rem', color: '#666' }}>
                              {parseFloat(payout.actualWagerAmount) < 2 && parseFloat(payout.payoutAmount) > 0 && (
                                <div>
                                  Normalized: ${((parseFloat(payout.payoutAmount) / parseFloat(payout.actualWagerAmount)) * 2).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Exacta Payout */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <h5 style={{ marginTop: 0 }}>Exacta</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Wager Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.exacta.actualWagerAmount}
                          onChange={(e) => updatePayout('exacta', 0, 'actualWagerAmount', e.target.value)}
                          className="form-control"
                          placeholder="2.00"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Payout Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.exacta.payoutAmount}
                          onChange={(e) => updatePayout('exacta', 0, 'payoutAmount', e.target.value)}
                          className="form-control"
                          placeholder="42.80"
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '0.85rem', color: '#666' }}>
                        {parseFloat(payouts.exacta.actualWagerAmount) < 2 && parseFloat(payouts.exacta.payoutAmount) > 0 && (
                          <div>
                            Normalized: ${((parseFloat(payouts.exacta.payoutAmount) / parseFloat(payouts.exacta.actualWagerAmount)) * 2).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trifecta Payout */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9f9f9', borderRadius: '4px' }}>
                    <h5 style={{ marginTop: 0 }}>Trifecta</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Wager Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.trifecta.actualWagerAmount}
                          onChange={(e) => updatePayout('trifecta', 0, 'actualWagerAmount', e.target.value)}
                          className="form-control"
                          placeholder="2.00"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Payout Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={payouts.trifecta.payoutAmount}
                          onChange={(e) => updatePayout('trifecta', 0, 'payoutAmount', e.target.value)}
                          className="form-control"
                          placeholder="123.40"
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: '0.85rem', color: '#666' }}>
                        {parseFloat(payouts.trifecta.actualWagerAmount) < 2 && parseFloat(payouts.trifecta.payoutAmount) > 0 && (
                          <div>
                            Normalized: ${((parseFloat(payouts.trifecta.payoutAmount) / parseFloat(payouts.trifecta.actualWagerAmount)) * 2).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Finish Time */}
                <div className="form-group">
                  <label htmlFor="finishTime">Finish Time (Optional)</label>
                  <input
                    type="text"
                    id="finishTime"
                    value={finishTime}
                    onChange={(e) => setFinishTime(e.target.value)}
                    placeholder="e.g., 2:01.25"
                    className="form-control"
                  />
                  <small className="form-text">Format: MM:SS.MS (e.g., 2:01.25)</small>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                type="submit"
                disabled={submitting || (!useAdvancedMode && !formData.winner) || (useAdvancedMode && standings.length === 0)}
                className="btn btn-primary"
              >
                {submitting ? 'Submitting Results...' : race.status === 'completed' ? 'Resubmit Results & Recalculate Payouts' : 'Submit Results & Calculate Payouts'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/races')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
      </>

      {race.horses && race.horses.length > 0 && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <h3>Horses in Race</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Horse</th>
                <th>Jockey</th>
                <th>Trainer</th>
                <th>Odds (Decimal)</th>
              </tr>
            </thead>
            <tbody>
              {race.horses.map(horse => (
                <tr key={horse.horseId}>
                  <td>#{horse.postPosition}</td>
                  <td>
                    <strong>{horse.horseName}</strong>
                    {race.results?.winner?.horseId === horse.horseId && (
                      <span style={{ marginLeft: '0.5rem', color: '#ffa000' }}>🏆</span>
                    )}
                    {race.results?.place?.horseId === horse.horseId && (
                      <span style={{ marginLeft: '0.5rem', color: '#c0c0c0' }}>🥈</span>
                    )}
                    {race.results?.show?.horseId === horse.horseId && (
                      <span style={{ marginLeft: '0.5rem', color: '#cd7f32' }}>🥉</span>
                    )}
                  </td>
                  <td>{horse.jockey || '-'}</td>
                  <td>{horse.trainer || '-'}</td>
                  <td>{horse.odds?.decimal || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SubmitResults;
